import { UnitConverter } from '../base/UnitConverter';
import { UnitOptions } from '../constants/unit.options';
import { CountPolicies, CountPolicy } from '../policy';
import { QuantityResult } from '../types';

export class MoneyUnitConverter extends UnitConverter<
  (typeof UnitOptions)['Money'][number]['value']
> {
  // 싱글턴 인스턴스
  private static instance: MoneyUnitConverter;

  private constructor() {
    super();
  }

  static getInstance(): MoneyUnitConverter {
    if (!MoneyUnitConverter.instance) {
      MoneyUnitConverter.instance = new MoneyUnitConverter();
    }
    return MoneyUnitConverter.instance;
  }

  protected units = [
    'won (₩)',
    'nanodollar (n$)',
    'microdollar (μ$)',
    'cent (¢)',
    'dollar ($)',
    'yen (¥)',
    'yuan (¥)',
  ] as const;

  protected unitToBase = {
    'won (₩)': 1,
    'nanodollar (n$)': 1e-9,
    'microdollar (μ$)': 1e-6,
    'cent (¢)': 1e-2,
    'dollar ($)': 1,
    'yen (¥)': 1,
    'yuan (¥)': 1,
  };

  // 정책 기본값 disabled로 override
  override resolveCountPolicy(countPolicy?: CountPolicy): CountPolicy {
    return countPolicy ?? CountPolicies.countConversionDisabled;
  }

  override convertToBestUnit(
    _value: number,
    _fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    return policy(_value, _fromUnit);
  }

  private unitToDollar: Record<string, number> = {
    'nanodollar (n$)': 1e-9,
    'microdollar (μ$)': 1e-6,
    'cent (¢)': 0.01,
    'dollar ($)': 1,
  };

  private convertDollar(
    value: number,
    fromUnit: keyof typeof this.unitToDollar,
    toUnit: keyof typeof this.unitToDollar,
  ): QuantityResult {
    // from·to 비율을 한 번에 곱한다. value * a / b 처럼 곱·나누기를 2회 하면 FP 오차가
    // 누적되지만(예: nano→micro), 비율을 먼저 구하면 마지막 자리 드리프트가 줄어든다.
    // 동일 단위는 ratio === 1(IEEE 754에서 x/x는 정확)이라 value를 그대로 반환한다.
    const ratio = this.unitToDollar[fromUnit] / this.unitToDollar[toUnit];
    const result = value * ratio;

    return { quantity: result, unit: toUnit, countUnit: '' };
  }

  static isDollarUnit(unit: string): boolean {
    return unit in MoneyUnitConverter.getInstance().unitToDollar;
  }

  override convertToUnit(
    value: number,
    fromUnit: string,
    toUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    if (fromUnit in this.unitToDollar && toUnit in this.unitToDollar) {
      return this.convertDollar(value, fromUnit, toUnit);
    }
    const policy = this.resolveCountPolicy(countPolicy);

    return policy(value, toUnit);
  }

  override convertToLargerBestUnit(
    value: number,
    fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    return policy(value, fromUnit);
  }
}
